import {
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type PropsWithChildren,
  type ReactNode,
} from 'react'
import type { Components } from 'react-markdown'
import { Mic, ChevronRight } from 'lucide-react'
import { twMerge } from 'tailwind-merge'
import type { MeetingAppName } from '@highlight/common'
import { Markdown, Tooltip } from '@highlight/ui'

import { MeetingIcon } from '@renderer/chat'
import { useAudioNoteById } from '@renderer/hooks/audio-notes'
import { ACTION_HEADER_INDENT_CLASS } from '@renderer/overlay/Sidepanel/components/action-header'
import { MEETING_VIEW_ID } from '@renderer/overlay/Sidepanel/components/meeting/meeting-view-id'
import { KNOWN_MEETING_APPS } from '@renderer/overlay/Sidepanel/utils/activity-helpers'
import { useViewActions } from '../../../view-atoms'

const inline = ({ children }: PropsWithChildren) => <span>{children}</span>

/** A caption is one run of text: blocks become spans, so the pill can truncate them on a single line. */
const FLAT_COMPONENTS: Components = {
  p: inline,
  ul: inline,
  ol: inline,
  li: inline,
  blockquote: inline,
  h1: inline,
  h2: inline,
  h3: inline,
  h4: inline,
  h5: inline,
  h6: inline,
  br: () => null,
}

/**
 * The small rounded caption that sits under a title in the output view. The
 * chat's closing text and the meeting a workflow came from both wear it, so
 * the two read as one row of provenance rather than two unrelated controls.
 */
function CaptionPill({
  label,
  tooltip,
  expanded,
  emphasis = 'quote',
  frame = 'pill',
  onActivate,
  children,
}: {
  label: string
  tooltip: string
  expanded?: boolean
  /** `quote` sets the chat's words in italics; `label` is upright medium weight for a named source. */
  emphasis?: 'quote' | 'label'
  frame?: 'pill' | 'plain'
  onActivate: () => void
  children: ReactNode
}) {
  const handleClick = (event: ReactMouseEvent<HTMLDivElement>) => {
    if (event.target instanceof Element && event.target.closest('a, button')) {
      return
    }
    const selection = window.getSelection()
    if (selection && !selection.isCollapsed) {
      return
    }
    onActivate()
  }

  const handleKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget || (event.key !== 'Enter' && event.key !== ' ')) {
      return
    }
    event.preventDefault()
    onActivate()
  }

  return (
    <Tooltip>
      <Tooltip.Trigger
        render={
          <div
            role="button"
            tabIndex={0}
            aria-label={label}
            aria-expanded={expanded}
            onClick={handleClick}
            onKeyDown={handleKeyDown}
            className={twMerge(
              'text-foreground-secondary hover:text-foreground type-ui-xs inline-flex w-fit min-w-0 max-w-full cursor-pointer items-center gap-2 transition-colors duration-300',
              frame === 'pill'
                ? 'bg-element hover:bg-element-hover focus-visible:bg-element-hover aria-expanded:bg-element-hover corner-squircle min-h-8 truncate rounded-[var(--radius-size-lg)] px-2.5 leading-none'
                : 'aria-expanded:text-foreground corner-smooth rounded-sm leading-[150%]',
              emphasis === 'quote' ? 'italic' : 'font-medium',
            )}
          >
            {children}
          </div>
        }
      />
      <Tooltip.Content side="bottom">{tooltip}</Tooltip.Content>
    </Tooltip>
  )
}

export function AssistantText({
  text,
  frame = 'pill',
  threadOpen,
  onOpenThread,
}: {
  text: string | undefined
  frame?: 'pill' | 'plain'
  threadOpen: boolean
  onOpenThread: () => void
}) {
  return (
    <CaptionPill
      label="Open chat thread"
      tooltip="Open Chat thread"
      expanded={threadOpen}
      frame={frame}
      onActivate={onOpenThread}
    >
      {text && (
        <div className="min-w-0 flex-1 [overflow-wrap:anywhere]">
          <Markdown
            size="inherit"
            components={frame === 'pill' ? FLAT_COMPONENTS : undefined}
            className={frame === 'pill' ? 'block truncate whitespace-nowrap' : '[&_p+p]:mt-1 [&_p]:m-0'}
          >
            {text}
          </Markdown>
        </div>
      )}
    </CaptionPill>
  )
}

/** A turn's closing text under the card it landed on. */
export function OutputCaption({
  text,
  standalone,
  threadOpen,
  onOpenThread,
}: {
  text: string
  standalone: boolean
  threadOpen: boolean
  onOpenThread: () => void
}) {
  return (
    // Standalone captions need no chip to separate them from adjacent action rows.
    <div className={standalone ? 'px-4 pb-3' : twMerge('pb-3 pr-12', ACTION_HEADER_INDENT_CLASS)}>
      <AssistantText
        text={text}
        frame={standalone ? 'plain' : 'pill'}
        threadOpen={threadOpen}
        onOpenThread={onOpenThread}
      />
    </div>
  )
}

export function OutputMeetingSource({ audioNoteId }: { audioNoteId: string }) {
  const { data: audioNote } = useAudioNoteById(audioNoteId)
  const { openView } = useViewActions()
  const title = audioNote?.title ?? 'Meeting note'
  // Same resolution as the meeting view header: the meeting app's own icon
  // when the note came from a known app, the waveform otherwise.
  const appName = audioNote?.meeting?.app?.name
  const icon =
    appName && KNOWN_MEETING_APPS.includes(appName) ? (
      <span className="flex size-3 shrink-0 items-center justify-center" aria-hidden="true">
        <MeetingIcon appName={appName as MeetingAppName} size={12} />
      </span>
    ) : (
      <Mic className="size-3 shrink-0" aria-hidden="true" />
    )

  return (
    <CaptionPill
      label={`Open meeting: ${title}`}
      tooltip="Open meeting"
      emphasis="label"
      onActivate={() => openView(MEETING_VIEW_ID, { mode: 'note', audioNoteId, title }, { sourceLabel: 'Output' })}
    >
      {icon}
      <span className="min-w-0 flex-1 truncate">{title}</span>
      <ChevronRight className="-mr-0.5 size-3 shrink-0" aria-hidden="true" />
    </CaptionPill>
  )
}
